import getMethods from './../helpers/__base/getMethods';
import kebabize from './../helpers/__base/kebabize';
import getEntityMethodMessage from './getEntityMethodMessage';

export interface IMiddlewareParams {
  apiServiceParams?: unknown;
  hasAlertError?: boolean;
  hasAlertSuccess?: boolean;
  isManual?: boolean;
  methodNameAlertMap?: Record<string, any>;
}

export type EntityApiMiddlewareType = any;

/**
 * Create apiMiddleware for entity
 * @param {string} entityIdCamelized - Entity id in camelCase
 * @param {object} apiSettings - API settings
 * @param {object} apiServices - Object with apiServices for middleware
 * @returns {function: (boolean | object)} Object with functions - wrappers for apiService methods
 */
export default function createEntityApiMiddleware(
  entityIdCamelized: string,
  apiSettings: Record<string, any> = {},
  apiServices: Record<string, any> = {},
  setAlert: ({ message, type }: { message: string; type: string }) => void
) {
  /* Extract entities info for messaging */
  const entitiesNamesDeclensions = apiSettings.entitiesNamesDeclensions || {};
  const entitiesMessages = apiSettings.entitiesMessages || {};

  /* Extract messages info */
  const entityIdKebabized = kebabize(entityIdCamelized);
  const entityMessages = entitiesMessages[entityIdKebabized] || {};
  const defaultMessages = entitiesMessages._;

  /* Define result */
  return ({
    apiServiceParams = {},
    hasAlertError = true,
    hasAlertSuccess,
    isManual,
    methodNameAlertMap = {},
  }: IMiddlewareParams = {}) => {
    /* Create entity apiService */
    const apiServiceFactory = apiServices[entityIdCamelized];
    const apiService = apiServiceFactory(apiServiceParams);

    /* Define methods */
    const methodsNames = getMethods(apiService).filter(m => m[0] !== '_');
    methodsNames.sort();

    const functions: Record<string, any> = {};

    try {
      for (const methodName of methodsNames) {
        functions[methodName] = async (...params) => {
          /* Call method */
          const apiServiceResponse = await apiService[methodName](...params);
          if (isManual || methodName === 'init') return apiServiceResponse;

          /* Define behavior */
          let messageType;
          let needToAlert;
          let needToStop;
          if (apiServiceResponse?.status) {
            messageType = 'success';
            needToAlert =
              methodNameAlertMap[methodName]?.hasAlertSuccess ||
              hasAlertSuccess;
          } else {
            messageType = 'error';
            needToAlert =
              methodNameAlertMap[methodName]?.hasAlertError || hasAlertError;
            needToStop = true;
          }

          /* Alert if needed */
          if (needToAlert && setAlert) {
            const message = getEntityMethodMessage(
              entityMessages,
              methodName,
              messageType,
              defaultMessages,
              entitiesNamesDeclensions[entityIdKebabized]
            );
            if (message) setAlert({ message, type: messageType });
          }
          if (needToStop) return false;

          return apiServiceResponse.response || true;
        };
      }
    } catch (error) {
      console.warn('createApiMiddleware', entityIdCamelized, error.message);
    }

    return functions;
  };
}
